/**
 * Live clock — spec 011, option C (the JS half).
 *
 * Fills every `<time data-live-clock>` on the page with the current time in an
 * explicit IANA zone. Built for the footer (spec 054) and written to be the same
 * module the homepage hero uses when 011 lands — the element carries all of its
 * configuration on data-* attributes, so this file knows nothing about either
 * placement.
 *
 * Rules this file exists to honour (011 §4, AGENTS.md §Multilingual):
 *
 *   - ZERO user-facing strings. The aria-label arrives pre-translated from Liquid
 *     on `data-time-label-template`, with `[time]` as the interpolation slot —
 *     the same convention Horizon uses for `data-label-results-count`.
 *   - The locale tag is NOT pinned. It arrives on `data-locale` from
 *     `request.locale.iso_code`. Only the hour cycle is pinned.
 *   - The zone is passed explicitly. A bare `toLocaleTimeString()` returns the
 *     VISITOR's time — Tokyo time for a customer in Tokyo — which is the single
 *     most likely bug here and is invisible to anyone testing from Amsterdam.
 *   - No offset arithmetic. No `+1`, no `+2`, no "is it summer" branch. Intl
 *     applies the tz database, including the October hour that happens twice.
 *   - No library. `moment`/`dayjs`/`luxon`/`date-fns-tz` are all banned for this;
 *     Intl carries the full IANA database in every browser we support.
 *
 * Timer: minute-aligned and self-re-arming, not `setInterval(…, 1000)`. It wakes
 * the main thread 60× less often than a per-second interval, which is real battery
 * on the mid-range Android that AGENTS.md §Quality gates makes the primary target.
 * It also pauses while the document is hidden and resyncs on return, so a tab
 * restored after an hour never shows an hour-old time.
 *
 * Not a motion effect: nothing here animates, so it is not gated on
 * `html[data-motion]` and it is not counted in spec 060 §6.1's motion-JS budget.
 * It adds no scroll listener and no resize listener (060 §6.3 caps those at 1 and 0).
 */

/** @type {{ el: HTMLTimeElement, display: Intl.DateTimeFormat, machine: Intl.DateTimeFormat, label: string }[]} */
let clocks = [];
/** @type {{ el: HTMLTimeElement, display: Intl.DateTimeFormat, machine: Intl.DateTimeFormat }[]} */
let dates = [];
let timer = null;

/**
 * `hourCycle: 'h23'` rather than `hour12: false`. They are not the same: with
 * `hour12: false` some locale/ICU pairs resolve to the h24 cycle and render
 * midnight as "24:00" — which is both wrong-looking and 5 characters of a
 * different shape. h23 guarantees 00–23, so the string is always exactly "HH:MM"
 * and the reserved 5ch box in the CSS is always exact.
 */
const collect = () => {
  clocks = [];
  for (const el of document.querySelectorAll('time[data-live-clock]')) {
    const zone = el.dataset.timezone;
    if (!zone) continue;
    const locale = el.dataset.locale || undefined;

    clocks.push({
      el,
      display: new Intl.DateTimeFormat(locale, {
        timeZone: zone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
      /* Machine-readable only — never displayed, so pinning en-CA is correct
       * rather than a hardcoded display locale: it is the one tag that yields
       * YYYY-MM-DD, which is what the datetime attribute has to be. */
      machine: new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
        timeZoneName: 'longOffset',
      }),
      label: el.dataset.timeLabelTemplate || '',
    });
  }

  /* ── The DATE half — added by U1 for the hero (spec 011 option C) ──────────
   * Additive: the footer (054) renders no `[data-live-date]` element, so this
   * loop finds nothing there and its behaviour is byte-for-byte unchanged.
   *
   * The footer declined to build this half and gave the reason: rendering a
   * locale-correct date server-side would mean adding a `date_formats` group to
   * three locale files, and `{{ 'now' | date: '%A %-d %B %Y' }}` is not an
   * option because `%A`/`%B` emit ENGLISH names on every storefront — the exact
   * defect `000-i18n-audit.md` §4.1 names spec 011 for.
   *
   * The hero DOES show a date, so it is solved here instead of in Liquid. Liquid
   * renders a locale-NEUTRAL ISO date, which is complete and can never be wrong
   * without JS; `Intl` then upgrades it in place to the long localised form.
   * That needs no untestable claim about what Shopify's `date` filter does with
   * `date_formats` — which matters, because there is no store to verify against.
   *
   * The locale tag is never pinned: it arrives on `data-locale` from
   * `request.locale.iso_code`, same as the time.                              */
  dates = [];
  for (const el of document.querySelectorAll('time[data-live-date]')) {
    const zone = el.dataset.timezone;
    if (!zone) continue;
    const locale = el.dataset.locale || undefined;

    dates.push({
      el,
      display: new Intl.DateTimeFormat(locale, {
        timeZone: zone,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
      /* en-CA for the same reason as above: it is the tag that yields YYYY-MM-DD,
       * and nothing it produces is ever displayed. */
      machine: new Intl.DateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }),
    });
  }
};

const render = () => {
  const now = new Date();

  for (const clock of clocks) {
    const text = clock.display.format(now);
    clock.el.textContent = text;

    /* `longOffset` gives "GMT+02:00"; the datetime attribute wants "+02:00". */
    const parts = Object.fromEntries(
      clock.machine.formatToParts(now).map((part) => [part.type, part.value])
    );
    const offset = (parts.timeZoneName || '').replace('GMT', '') || 'Z';
    clock.el.dateTime = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}${offset}`;

    /* Deliberately NOT a live region (011 §5): a clock that announces itself every
     * minute interrupts whatever the user is reading, forever. The label exists so
     * the number is not encountered without its place. */
    if (clock.label) clock.el.setAttribute('aria-label', clock.label.replace('[time]', text));
  }

  for (const date of dates) {
    date.el.textContent = date.display.format(now);
    date.el.dateTime = date.machine.format(now);
  }
};

const stop = () => {
  if (timer !== null) clearTimeout(timer);
  timer = null;
};

const tick = () => {
  render();
  /* Re-align every tick rather than adding 60000 to the last one, so the clock
   * cannot drift and cannot land a second early and repeat a minute. */
  timer = setTimeout(tick, 60000 - (Date.now() % 60000) + 50);
};

const start = () => {
  stop();
  if (clocks.length || dates.length) tick();
};

document.addEventListener('visibilitychange', () => {
  if (document.hidden) stop();
  else start();
});

/* The theme editor re-renders a section without re-executing this module (same
 * module URL evaluates once per document), so the elements have to be re-collected
 * when a section comes back or the clock is blank until reload. */
document.addEventListener('shopify:section:load', () => {
  collect();
  start();
});

collect();
start();
